import isEmpty from "lodash-es/isEmpty";
import {
  BulkActionCount,
  DisplayFieldErrors,
  EfsMelcoEfsModelsFormat,
  EfsMelcoEfsModelsMappingFileAttribute,
  EfsMelcoEfsModelsMappingGroup,
  useGenericDrawer,
  useTableSelection,
} from "melco-shared-logic";
import {
  BulkActionContainerCol,
  Button,
  DesignTokens,
  FullWidthSpace,
  PlusIcon,
  Space,
  Table,
  TableActionBarRow,
  Title,
  ValueOrFallback,
} from "melco-ui";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { useDesignMappingDataAssignments } from "../../hooks/designMappings/useDesignMappingDataAssignments";
import { useFoldernameRules } from "../../hooks/designMappings/useFoldernameRules";
import { useBasicSettings } from "../../hooks/settings/useBasicSettings";
import { BulkActionRemoveFoldernameRules } from "./BulkActionRemoveFoldernameRules";
import { EmptyWrapper } from "../shared/EmptyWrapper";
import { DisplayFoldernameRuleValue } from "./DisplayFoldernameRuleValue";
import { FoldernameRuleDrawer } from "./FoldernameRuleDrawer";

const indexAsRowKey = (_: unknown, idx: number | undefined) => idx!.toString();

const { spacings } = DesignTokens;

const TableWrapper = styled.div`
  margin-left: -${spacings.xlarge}px;
  margin-right: -${spacings.xlarge}px;
`;

const AddButtonWrapper = styled.div`
  display: flex;
  justify-content: center;
`;

type FoldernameRulesProps = {
  inputFormat: EfsMelcoEfsModelsFormat | undefined;
};

export const FoldernameRules: React.FC<FoldernameRulesProps> = ({
  inputFormat,
}) => {
  const { t } = useTranslation();

  const { settings } = useBasicSettings();

  const dataAssignments = useDesignMappingDataAssignments(inputFormat);

  const { foldernameRules } = useFoldernameRules();

  const foldernameRulesDrawer = useGenericDrawer();
  const { openDrawer } = foldernameRulesDrawer;

  const [editFoldernameRuleIndex, setEditFoldernameRuleIndex] = useState<
    number | undefined
  >(undefined);

  const { selectedRowKeys, setSelectedRowKeys, rowSelection, onRow } =
    useTableSelection((row: EfsMelcoEfsModelsMappingGroup) => {
      setEditFoldernameRuleIndex(foldernameRules?.findIndex((r) => r === row));
      openDrawer();
    });

  const columns = [
    {
      title: t("designMappings.actions.foldernameRules.list.columns.type"),
      key: "type",
      dataIndex: "type",
      render: (_: string, item: EfsMelcoEfsModelsMappingFileAttribute) => {
        return (
          <ValueOrFallback
            value={
              item.type
                ? t(`designMappings.actions.foldernameRules.types.${item.type}`)
                : undefined
            }
            fallback={t("global.fallback.empty")}
          />
        );
      },
    },
    {
      title: t("designMappings.actions.foldernameRules.list.columns.value"),
      key: "value",
      dataIndex: "value",
      render: (_: string, item: EfsMelcoEfsModelsMappingFileAttribute) => (
        <DisplayFoldernameRuleValue
          filenameRule={item}
          dataAssignments={dataAssignments}
        />
      ),
    },
  ];

  const handleAdd = useCallback(() => {
    setEditFoldernameRuleIndex(undefined);
    openDrawer();
  }, [openDrawer, setEditFoldernameRuleIndex]);

  const addButton = (
    <Button type="primary" icon={<PlusIcon />} onClick={handleAdd}>
      {t("designMappings.actions.foldernameRules.actions.add.button")}
    </Button>
  );

  const isFolderRulesFeatureEnabled = settings?.enable_folder_grouping === true;

  if (!isFolderRulesFeatureEnabled) {
    return null;
  }

  return (
    <>
      <Title level={5}>
        {t("designMappings.detail.form.foldernameRules.title")}
      </Title>

      <DisplayFieldErrors name="folder_groups" aggregateErrors />

      {isEmpty(foldernameRules) ? (
        <FullWidthSpace direction="vertical">
          <EmptyWrapper data-cy-id="foldername-rules-empty-wrapper">
            {addButton}
          </EmptyWrapper>
        </FullWidthSpace>
      ) : (
        <>
          <Space direction="vertical">
            <TableWrapper>
              <TableActionBarRow>
                <BulkActionContainerCol>
                  <BulkActionCount selectedCount={selectedRowKeys.length} />
                  <BulkActionRemoveFoldernameRules
                    selectedRowKeys={selectedRowKeys}
                    setSelectedRowKeys={setSelectedRowKeys}
                  />
                </BulkActionContainerCol>
              </TableActionBarRow>
              <Table
                showSorterTooltip={false}
                sticky
                dataSource={foldernameRules ?? []}
                columns={columns}
                rowKey={indexAsRowKey}
                rowSelection={rowSelection}
                onRow={onRow}
                rowClassName={() => "clickable"}
                pagination={false}
                data-cy-id="foldername-rules-table"
              />
            </TableWrapper>
            <AddButtonWrapper>{addButton}</AddButtonWrapper>
          </Space>
        </>
      )}

      {inputFormat && (
        <FoldernameRuleDrawer
          drawer={foldernameRulesDrawer}
          editFoldernameRuleIndex={editFoldernameRuleIndex}
          dataAssignments={dataAssignments}
        />
      )}
    </>
  );
};
